<template>
  <v-app v-if="resetPasswordForm">
    <v-main>
      <v-container fluid fill-height>
        <v-layout align-center justify-center>
          <v-flex xs12 sm8 md4>
            <div class="d-flex flex-column">
              <div style="text-align: center; padding-bottom: 30px;">
                <img height="100px" :src="require(`@/assets/${skin.loginLogo}`)" :alt="skin.name">
              </div>
              <v-card class="elevation-12">
                <v-toolbar dark color="primary">
                  <v-toolbar-title>{{ $t('views.App.setNewPassword') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <v-form>
                    <v-text-field id="newPassword" prepend-icon="mdi-lock" name="newPassword" :label="$t('views.App.newPassword')" type="password" v-model="newPassword" v-on:keyup.enter="setNewPassword" @input="logInError = ''" :disabled="tryingSetNewPassword"></v-text-field>
                    <v-text-field id="confirmNewPassword" prepend-icon="mdi-lock" name="confirmNewPassword" :label="$t('views.App.confirmNewPassword')" type="password" v-model="confirmNewPassword" v-on:keyup.enter="setNewPassword" @input="logInError = ''" :disabled="tryingSetNewPassword"></v-text-field>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" @click="setNewPassword" :disabled="tryingSetNewPassword || invalidNewPassword || invalidConfirmNewPassword">
                    <span v-if="!tryingSetNewPassword">{{ $t('views.App.setNewPassword') }}</span>
                    <span v-else><v-progress-circular indeterminate color="primary"></v-progress-circular></span>
                  </v-btn>
                </v-card-actions>
              </v-card>
              <div style="text-align: center; padding-top: 20px;">
                <v-dialog v-model="dialogResetPassword" hide-overlay width="500">
                  <template v-slot:activator="{ on: dialog }">
                    <a v-if="!resetPasswordForm" href="#" v-on="{...dialog}">{{ $t('views.App.forgotPassword') }}</a>
                    <div v-else>
                      <v-alert
                        type="info"
                        :title="$t('views.App.passwordRequirementsTitle')"
                        text
                        variant="tonal"
                        class="text-left"
                      >
                        {{ $t('views.App.passwordRequirementsIntro') }}
                        <ul>
                          <li><b>{{ $t('views.App.passwordReqLength') }}</b></li>
                          <li>{{ $t('views.App.passwordReqNumber') }}</li>
                          <li>{{ $t('views.App.passwordReqLowercase') }}</li>
                          <li>{{ $t('views.App.passwordReqUppercase') }}</li>
                          <li>{{ $t('views.App.passwordReqSpecial') }} <i>{{ $t('views.App.passwordReqSpecialChars') }}</i></li>
                        </ul>
                      </v-alert>
                    </div>
                    <v-snackbar
                      v-model="snackbarResetPassword"
                      :timeout="-1"
                      :value="true"
                      elevation="24"
                      color="primary"
                      text
                    >
                      {{ $t('views.App.checkEmailForReset') }}
                      <template v-slot:action="{ attrs }">
                        <v-btn
                          color="primary"
                          text
                          v-bind="attrs"
                          @click="snackbarResetPassword = false"
                        >
                          {{ $t('views.App.ok') }}
                        </v-btn>
                      </template>
                    </v-snackbar>
                  </template>
                    <v-card>
                      <v-card-title
                        class="text-h5"
                        primary-title
                      >
                        {{ $t('views.App.resetPassword') }}
                      </v-card-title>
                      <v-card-text>
                        <v-text-field
                          v-model="logInEmail"
                          :label="$t('views.App.enterYourEmail')"
                          required
                          autofocus
                        ></v-text-field>
                      </v-card-text>
                      <v-divider></v-divider>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="warning"
                          text
                          @click="dialogResetPassword = false"
                        >
                          {{ $t('views.App.cancel') }}
                        </v-btn>
                        <v-btn
                          :disabled="logInEmail.length === 0"
                          color="primary"
                          text
                          @click="dialogResetPassword = false; resetEmail()"
                        >
                          {{ $t('views.App.sendResetEmail') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                </v-dialog>
              </div>
              <v-alert v-if="logInError" :value="true" type="error">
                {{ logInError }}
              </v-alert>
              <div style="text-align: center; padding-top: 40px;">
                <img v-if="skin.footerLogo" :height="skin.footerHeight" :src="require(`@/assets/${skin.footerLogo}`)">
              </div>
              <div style="text-align: center; padding-top: 10px;">
                <span class="overline">{{ skin.copyright }} <a href="https://www.noisegrasp.com" target="_blank">NoiseGrasp</a>©</span>
                <div class="overline"><a href="#" @click="showTermsDialog = true">{{ $t('views.App.termsAndConditions') }}</a></div>
                <div class="text--disabled caption">{{ $gitHash ? $gitHash : '.' }}</div>
                <div style="margin-top: 8px;">
                  <v-select
                    v-model="selectedLanguage"
                    :items="languages"
                    item-text="name"
                    item-value="code"
                    dense
                    outlined
                    hide-details
                    style="max-width: 120px; margin: 0 auto;"
                    class="caption"
                  ></v-select>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
  </v-app>
  <v-app v-else-if="isSharedRoute">
    <v-main>
      <router-view />
    </v-main>
  </v-app>
  <v-app v-else-if="shouldRenderMainApp && authUser.isLoggedIn">
    <v-dialog v-model="resetPasswordDialog" max-width="600" persistent>
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('views.App.setANewPassword') }}
        </v-card-title>
        <v-card-text v-if="!forgotPasswordMessage">
          <v-form ref="setPasswordForm">
            <v-text-field id="currentPassword" name="currentPassword" :label="$t('views.App.currentPassword')" :rules="currentPasswordRules" :type="showCurrentPassword ? 'text' : 'password'" v-model="currentPassword" @input="wrongCurrentPassword = false">
              <template v-slot:append>
                <v-btn icon :color="showCurrentPassword ? 'primary' : ''" @click="showCurrentPassword = !showCurrentPassword"><v-icon>mdi-eye</v-icon></v-btn>
              </template>
            </v-text-field>
            <v-text-field id="newPassword" name="newPassword" :label="$t('views.App.newPassword')" :type="showNewPassword ? 'text' : 'password'" v-model="newPassword">
              <template v-slot:append>
                <v-btn icon :color="showNewPassword ? 'primary' : ''" @click="showNewPassword = !showNewPassword"><v-icon>mdi-eye</v-icon></v-btn>
              </template>
            </v-text-field>
            <v-text-field id="newPassword2" name="newPassword2" :label="$t('views.App.repeatNewPassword')" :rules="newPasswordRules" :type="showNewPassword ? 'text' : 'password'" v-model="newPassword2"></v-text-field>
            <div class="d-flex justify-end">
              <v-btn color="primary" @click="setNewPasswordFromDialog" :disabled="$refs.setPasswordForm ? !$refs.setPasswordForm.validate() : false">{{ $t('views.App.proceed') }}</v-btn>
            </div>
            <div>
              <a @click.stop="forgotPassword">{{ $t('views.App.forgotCurrentPassword') }}</a>
            </div>
          </v-form>
        </v-card-text>
        <v-card-text v-else>
          <div>{{ $t('views.App.passwordResetEmailSent') }}</div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showTermsDialog" max-width="800" :persistent="!authUser.acceptedTerms">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="showTermsDialog = false" v-if="authUser.acceptedTerms">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('views.App.termsAndConditions') }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pa-6">
          <div class="text-h6 mb-4">{{ $t('views.App.termsTitle') }}</div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection1Title') }}</div>
            <p><strong>"User"</strong> {{ $t('views.App.termsSection1User').replace('"User" ', '') }}</p>
            <p><strong>"Services"</strong> {{ $t('views.App.termsSection1Services').replace('"Services" ', '') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection2Title') }}</div>
            <p>{{ $t('views.App.termsSection2Content') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection3Title') }}</div>
            <p>{{ $t('views.App.termsSection3Intro') }}</p>
            <ul class="ml-4">
              <li>{{ $t('views.App.termsSection3Item1') }}</li>
              <li>{{ $t('views.App.termsSection3Item2') }}</li>
              <li>{{ $t('views.App.termsSection3Item3') }}</li>
            </ul>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection4Title') }}</div>
            <p>{{ $t('views.App.termsSection4Intro') }}</p>
            <ul class="ml-4">
              <li>{{ $t('views.App.termsSection4Item1') }}</li>
              <li>{{ $t('views.App.termsSection4Item2') }}</li>
              <li>{{ $t('views.App.termsSection4Item3') }}</li>
              <li>{{ $t('views.App.termsSection4Item4') }}</li>
            </ul>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection5Title') }}</div>
            <p>{{ $t('views.App.termsSection5Content1') }}</p>
            <p>{{ $t('views.App.termsSection5Content2') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection6Title') }}</div>
            <p>{{ $t('views.App.termsSection6Content') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection7Title') }}</div>
            <p>{{ $t('views.App.termsSection7Intro') }}</p>
            <ul class="ml-4">
              <li>{{ $t('views.App.termsSection7Item1') }}</li>
              <li>{{ $t('views.App.termsSection7Item2') }}</li>
              <li>{{ $t('views.App.termsSection7Item3') }}</li>
              <li>{{ $t('views.App.termsSection7Item4') }}</li>
            </ul>
            <p>{{ $t('views.App.termsSection7Content') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection8Title') }}</div>
            <p>{{ $t('views.App.termsSection8Content1') }}</p>
            <p>{{ $t('views.App.termsSection8Content2') }}</p>
            <p>{{ $t('views.App.termsSection8Content3') }}</p>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t('views.App.termsSection9Title') }}</div>
            <p>{{ $t('views.App.termsSection9Content') }}</p>
          </div>

          <div class="text-caption text--secondary mt-6">
            {{ $t('views.App.termsLastUpdated') }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!authUser.acceptedTerms"
            color="primary"
            @click="acceptTerms"
            :loading="acceptingTerms"
            :disabled="acceptingTerms"
          >
            {{ $t('views.App.acceptTerms') }}
          </v-btn>
          <v-btn
            v-else
            color="primary"
            text
            @click="showTermsDialog = false"
          >
            {{ $t('views.App.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showNewsDialog" max-width="600">
      <v-card v-if="recentNews.length > 0">
        <v-card-title class="text-h5">
          {{ $t('views.App.whatsNew') }}
        </v-card-title>
        <v-card-text>
          <div v-if="recentNews.length > 0">
            <h3 class="mb-2">{{ recentNews[currentNewsIndex].title }}</h3>
            <p class="text-caption mb-4">{{ $t('views.App.posted', { time: humanTime(recentNews[currentNewsIndex].dateCreated.toDate()) }) }}</p>
            <p v-html="formattedContent(recentNews[currentNewsIndex].content)"></p>
            <div class="d-flex justify-space-between mt-10">
              <v-btn
                :disabled="currentNewsIndex === 0"
                @click="currentNewsIndex--"
              >
                {{ $t('views.App.previous') }}
              </v-btn>
              <span v-if="recentNews.length > 1">{{ $t('views.App.ofCount', { current: currentNewsIndex + 1, total: recentNews.length }) }}</span>
              <v-btn
                v-if="currentNewsIndex === recentNews.length - 1"
                @click="showNewsDialog = false"
                color="primary"
              >
                {{ $t('views.App.close') }}
              </v-btn>
              <v-btn v-else @click="currentNewsIndex++; showNewsPost(currentNewsIndex)">
                {{ $t('views.App.next') }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-navigation-drawer
      v-if="!plain"
      persistent
      v-model="drawer"
      enable-resize-watcher
      fixed
      app
      :dark="skin.dark"
      :color="skin.color"
    >
      <div>
        <v-layout pa-2 column fill-height class="lightbox white--text">
          <v-spacer></v-spacer>
          <v-flex shrink align-self-center>
            <img height="60px" v-show="currentClientLogo" :src="currentClientLogo" alt="Current model logo" class="pl-2 pr-2">
          </v-flex>
          <v-flex v-if="!token && !iframe" shrink align-self-center class="pt-1">
            <div class="subheading">{{ authUser.firstName }} {{ authUser.lastName }}</div>
          </v-flex>
          <v-flex v-if="!token && !iframe" shrink align-self-center>
            <div style="font-size: 11px;">{{ authUser.email }}</div>
          </v-flex>
        </v-layout>
      </div>
      <div id="current-model-menu">
        <v-dialog v-model="modelDialog" transition="dialog-top-transition">
          <v-card>
            <v-card-title class="headline">
              <div style="display: flex; align-items: center; width: 100%; gap: 16px;">
                <span style="white-space: nowrap;">{{ $t('views.App.modelSelection') }}</span>
                <v-spacer></v-spacer>
                <v-menu
                  v-if="typeModelDialog === 0 || typeModelDialog === 1"
                  v-model="countryFilterMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-height="400"
                  style="align-self: center;"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on: tooltipOn }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          v-on="{ ...on, ...tooltipOn }"
                          :color="hasActiveCountryFilter ? 'primary' : ''"
                          style="margin-top: 8px;"
                        >
                          <v-icon :color="hasActiveCountryFilter ? 'primary' : 'grey'">
                            mdi-earth
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ selectedCountries.length === 0 || selectedCountries.length === availableCountries.length
                           ? $t('views.App.allCountries')
                           : $t('views.App.countriesSelected', { count: selectedCountries.length, total: availableCountries.length }) }}
                      </span>
                    </v-tooltip>
                  </template>
                  <v-card>
                    <v-card-title class="pb-2">
                      <span>{{ $t('views.App.filterByCountry') }}</span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        small
                        @click="countryFilterMenu = false"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                      <v-text-field
                        v-model="countrySearch"
                        :label="$t('views.App.searchCountries')"
                        class="px-4 pt-2"
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                        clearable
                        dense
                        @click:clear="countrySearch = ''"
                      ></v-text-field>
                      <v-divider class="mt-2"></v-divider>
                      <v-list-item
                        ripple
                        @click="toggleSelectAll"
                      >
                        <v-list-item-action>
                          <v-icon :color="selectedCountries.length > 0 ? 'indigo darken-4' : ''">
                            {{ selectAllIcon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ selectAllText }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list dense max-height="250" class="overflow-y-auto">
                        <v-list-item
                          v-for="country in filteredCountries"
                          :key="country.value"
                          @click="toggleCountry(country.value)"
                        >
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="selectedCountries.includes(country.value)"
                              color="primary"
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ country.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <v-divider></v-divider>
                      <div class="px-4 py-2 caption grey--text">
                        {{ $t('views.App.countriesSelected', { count: selectedCountries.length, total: availableCountries.length }) }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <v-menu
                  v-if="typeModelDialog === 0 || (typeModelDialog === 1 && availableBrands.length > 0)"
                  v-model="brandFilterMenu"
                  :close-on-content-click="false"
                  offset-y
                  max-height="400"
                  style="align-self: center;"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on: tooltipOn }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          v-on="{ ...on, ...tooltipOn }"
                          :color="hasActiveBrandFilter ? 'primary' : ''"
                          style="margin-top: 8px;"
                        >
                          <v-icon :color="hasActiveBrandFilter ? 'primary' : 'grey'">
                            mdi-tag-multiple
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ selectedBrands.length === 0 || selectedBrands.length === availableBrands.length
                           ? $t('views.App.allBrands')
                           : $t('views.App.brandsSelected', { count: selectedBrands.length, total: availableBrands.length }) }}
                      </span>
                    </v-tooltip>
                  </template>
                  <v-card>
                    <v-card-title class="pb-2">
                      <span>{{ $t('views.App.filterByBrand') }}</span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        small
                        @click="brandFilterMenu = false"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                      <v-text-field
                        v-model="brandSearch"
                        :label="$t('views.App.searchBrands')"
                        class="px-4 pt-2"
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                        clearable
                        dense
                        @click:clear="brandSearch = ''"
                      ></v-text-field>
                      <v-divider class="mt-2"></v-divider>
                      <v-list-item
                        ripple
                        @click="toggleSelectAllBrands"
                      >
                        <v-list-item-action>
                          <v-icon :color="selectedBrands.length > 0 ? 'indigo darken-4' : ''">
                            {{ selectAllBrandsIcon }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ selectAllBrandsText }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list dense max-height="250" class="overflow-y-auto">
                        <v-list-item
                          v-for="brand in filteredBrands"
                          :key="brand.value"
                          @click="toggleBrand(brand.value)"
                        >
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="selectedBrands.includes(brand.value)"
                              color="primary"
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ brand.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <v-divider></v-divider>
                      <div class="px-4 py-2 caption grey--text">
                        {{ $t('views.App.brandsSelected', { count: selectedBrands.length, total: availableBrands.length }) }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
                <template v-if="typeModelDialog === 0">
                <v-menu
                  v-for="cfg in visibleMetadataConfigs"
                  :key="cfg.key"
                  v-model="metadataMenu[cfg.key]"
                  :close-on-content-click="false"
                  offset-y
                  max-height="400"
                  style="align-self: center;"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on: tooltipOn }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          v-on="{ ...on, ...tooltipOn }"
                          :color="hasActiveMetadataFilter(cfg.key) ? 'primary' : ''"
                          style="margin-top: 8px;"
                        >
                          <v-icon :color="hasActiveMetadataFilter(cfg.key) ? 'primary' : 'grey'">
                            {{ cfg.icon }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ metadataSelected[cfg.key].length === 0 || metadataSelected[cfg.key].length === availableMetadata(cfg.key).length
                           ? $t('views.App.' + cfg.allKey)
                           : $t('views.App.' + cfg.selectedKey, { count: metadataSelected[cfg.key].length, total: availableMetadata(cfg.key).length }) }}
                      </span>
                    </v-tooltip>
                  </template>
                  <v-card>
                    <v-card-title class="pb-2">
                      <span>{{ $t('views.App.' + cfg.filterKey) }}</span>
                      <v-spacer></v-spacer>
                      <v-btn
                        icon
                        small
                        @click="metadataMenu[cfg.key] = false"
                      >
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                      <v-text-field
                        v-model="metadataSearch[cfg.key]"
                        :label="$t('views.App.' + cfg.searchKey)"
                        class="px-4 pt-2"
                        prepend-inner-icon="mdi-magnify"
                        hide-details
                        clearable
                        dense
                        @click:clear="metadataSearch[cfg.key] = ''"
                      ></v-text-field>
                      <v-divider class="mt-2"></v-divider>
                      <v-list-item
                        ripple
                        @click="toggleSelectAllMetadata(cfg.key)"
                      >
                        <v-list-item-action>
                          <v-icon :color="metadataSelected[cfg.key].length > 0 ? 'indigo darken-4' : ''">
                            {{ selectAllMetadataIcon(cfg.key) }}
                          </v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ selectAllMetadataText(cfg.key) }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list dense max-height="250" class="overflow-y-auto">
                        <v-list-item
                          v-for="opt in filteredMetadata(cfg.key)"
                          :key="opt.value"
                          @click="toggleMetadata(cfg.key, opt.value)"
                        >
                          <v-list-item-action>
                            <v-checkbox
                              :input-value="metadataSelected[cfg.key].includes(opt.value)"
                              color="primary"
                            ></v-checkbox>
                          </v-list-item-action>
                          <v-list-item-content>
                            <v-list-item-title>{{ opt.text }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <v-divider></v-divider>
                      <div class="px-4 py-2 caption grey--text">
                        {{ $t('views.App.' + cfg.selectedKey, { count: metadataSelected[cfg.key].length, total: availableMetadata(cfg.key).length }) }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-menu>
                </template>
                <v-text-field
                  v-model="modelDialogSearch"
                  append-icon="mdi-magnify"
                  :label="$t('views.App.search')"
                  single-line
                  hide-details
                  style="max-width: 300px;"
                ></v-text-field>
              </div>
            </v-card-title>
            <v-card-text>
              <!-- Searches, sorts and pages the rows itself (server-items-length), so that a page holds whole
                   version families: modelDialogRows families per page, plus their expanded older versions -->
              <v-data-table
                :headers="modelDialogheaders"
                :items="modelDialogPageItems"
                :server-items-length="modelDialogFamilyCount"
                item-key="id"
                class="elevation-1 compact-model-table"
                :height="modelDialogTableHeight"
                dense
                :sort-by.sync="modelDialogSortBy"
                :sort-desc.sync="modelDialogSortDesc"
                :page.sync="modelDialogPage"
                :items-per-page="modelDialogRows"
                :footer-props="{
                  itemsPerPageText: '',
                  itemsPerPageOptions: [modelDialogRows]
                }"
                :no-data-text="modelDialogSearch ? '$vuetify.dataIterator.noResultsText' : '$vuetify.noDataText'"
                :item-class="modelDialogItemClass"
                @click:row="selectModel"
              >
                <template v-slot:top>
                  <v-tabs v-model="typeModelDialog">
                    <v-tab>{{ $t('views.App.adHocModels') }}</v-tab>
                    <v-tab>{{ $t('views.App.customModels') }}</v-tab>
                    <v-tab>{{ $t('views.App.automaticModels') }}</v-tab>
                  </v-tabs>
                </template>
                <template v-slot:item.date_created_formatted="{ item }">
                  <v-tooltip bottom v-if="dateModifiedDate(item)">
                    <template v-slot:activator="{ on }">
                      <v-chip x-small v-on="on">
                        <span v-if="modelDialogSearch" v-html="highlightText(dateModifiedDate(item), modelDialogSearch)"></span>
                        <span v-else>{{ dateModifiedDate(item) }}</span>
                      </v-chip>
                    </template>
                    <span>{{ dateModifiedTime(item) }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.name="{ item }">
                  <v-btn v-if="item.versionCount > 1" icon x-small class="mr-1" @click.stop="toggleVersionFamily(item)">
                    <v-icon small>{{ isVersionFamilyExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
                  </v-btn>
                  <!-- Same size as the chevron above, so older versions' names line up with their current version's -->
                  <v-btn v-else-if="item.isOlderVersion" icon x-small class="mr-1 version-row-spacer" tabindex="-1" aria-hidden="true">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-btn>
                  <span v-if="modelDialogSearch" v-html="highlightText(item.name, modelDialogSearch)"></span>
                  <span v-else>{{ item.name }}</span>
                  <v-tooltip bottom v-if="item.versionCount > 1 || item.isOlderVersion">
                    <template v-slot:activator="{ on }">
                      <v-chip x-small class="ml-1" :color="item.isOlderVersion ? '' : 'primary'" v-on="on">
                        v{{ versionNumber(item) }}<span v-if="!item.isOlderVersion">&nbsp;· {{ $t('views.App.current') }}</span>
                      </v-chip>
                    </template>
                    <div>{{ $t('views.App.versionDeployed', { date: versionDate(item) }) }}</div>
                    <div v-if="item.version_notes">{{ item.version_notes }}</div>
                  </v-tooltip>
                  <v-tooltip bottom v-show="authUser.isAdmin || authUser.isScientist">
                    <template v-slot:activator="{ on }">
                      <v-icon
                        color="blue"
                        v-show="item.model_status === 'Deployed'"
                        v-on="on"
                      >
                        mdi-flag-variant
                      </v-icon>
                    </template>
                    <span>{{ $t('views.App.deployed') }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.reference_model.config.category="{ item }">
                  <span v-if="modelDialogSearch && getNestedValue(item, 'reference_model.config.category')" v-html="highlightText(getNestedValue(item, 'reference_model.config.category'), modelDialogSearch)"></span>
                  <span v-else>{{ getNestedValue(item, 'reference_model.config.category') }}</span>
                </template>
                <template v-slot:item.custom_model.brand="{ item }">
                  <span v-if="modelDialogSearch && getNestedValue(item, 'custom_model.brand')" v-html="highlightText(getNestedValue(item, 'custom_model.brand'), modelDialogSearch)"></span>
                  <span v-else>{{ getNestedValue(item, 'custom_model.brand') }}</span>
                </template>
                <template v-slot:item.custom_model.countryName="{ item }">
                  <span v-if="modelDialogSearch && getNestedValue(item, 'custom_model.countryName')" v-html="highlightText(getNestedValue(item, 'custom_model.countryName'), modelDialogSearch)"></span>
                  <span v-else>{{ getNestedValue(item, 'custom_model.countryName') }}</span>
                </template>
                <template v-slot:item.created_by_name="{ item }">
                  <span v-if="modelDialogSearch && item.created_by_name" v-html="highlightText(item.created_by_name, modelDialogSearch)"></span>
                  <span v-else>{{ item.created_by_name }}</span>
                </template>
                <template v-slot:item.brandNames="{ item }">
                  <v-chip v-if="isNaValue(item.brandNames)" x-small color="grey lighten-3" text-color="grey" class="na-chip">{{ $t('views.App.notAvailable') }}</v-chip>
                  <span v-else-if="modelDialogSearch" v-html="highlightText(item.brandNames, modelDialogSearch)"></span>
                  <span v-else>{{ item.brandNames }}</span>
                </template>
                <template v-slot:item.countryName="{ item }">
                  <v-chip v-if="isNaValue(item.countryName)" x-small color="grey lighten-3" text-color="grey" class="na-chip">{{ $t('views.App.notAvailable') }}</v-chip>
                  <span v-else-if="modelDialogSearch" v-html="highlightText(item.countryName, modelDialogSearch)"></span>
                  <span v-else>{{ item.countryName }}</span>
                </template>
                <template v-slot:item.clientName="{ item }">
                  <v-chip v-if="isNaValue(item.clientName)" x-small color="grey lighten-3" text-color="grey" class="na-chip">{{ $t('views.App.notAvailable') }}</v-chip>
                  <span v-else-if="modelDialogSearch" v-html="highlightText(item.clientName, modelDialogSearch)"></span>
                  <span v-else>{{ item.clientName }}</span>
                </template>
                <template v-slot:item.categoryName="{ item }">
                  <v-chip v-if="isNaValue(item.categoryName)" x-small color="grey lighten-3" text-color="grey" class="na-chip">{{ $t('views.App.notAvailable') }}</v-chip>
                  <span v-else-if="modelDialogSearch" v-html="highlightText(item.categoryName, modelDialogSearch)"></span>
                  <span v-else>{{ item.categoryName }}</span>
                </template>
                <template v-slot:item.subcategoryName="{ item }">
                  <v-chip v-if="isNaValue(item.subcategoryName)" x-small color="grey lighten-3" text-color="grey" class="na-chip">{{ $t('views.App.notAvailable') }}</v-chip>
                  <span v-else-if="modelDialogSearch" v-html="highlightText(item.subcategoryName, modelDialogSearch)"></span>
                  <span v-else>{{ item.subcategoryName }}</span>
                </template>
              </v-data-table>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="red darken-1"
                text
                @click="modelDialog = false"
              >
                {{ $t('views.App.close') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-select
        :items="userClients"
        :label="$t('views.App.currentModel')"
        item-text="name"
        item-value="id"
        v-model="currentClientLocal"
        @mouseup.stop="modelDialog = true"
        readonly
        block
        class="pl-2 pr-2 mt-4"
        append-icon="mdi-swap-horizontal"
      >
        <template v-slot:selection="{ item }">
          <span v-if="item.type === 'mmm'">
            <v-icon>mdi-graphql</v-icon> {{ item.name }}
          </span>
          <span v-else>
            {{ item.name }}
          </span>
        </template>
        <template v-slot:item="{ item }">
          <span v-if="item.type === 'mmm'">
            <v-icon>mdi-graphql</v-icon> {{ item.name }}
          </span>
          <span v-else>
            {{ item.name }}
          </span>
        </template>
      </v-select>
      <div v-if="currentModelDoc" class="pl-2 pr-2 current-model-version">
        <model-version-badge :model="currentModelDoc" go-to-current />
      </div>
      </div>
      <v-list>
        <v-list-item link :to="{name: 'home'}">
          <v-list-item-icon>
            <v-icon>mdi-home</v-icon>
          </v-list-item-icon>
          <v-list-item-title id="home-menu">{{ $t('views.App.home') }}</v-list-item-title>
        </v-list-item>
      </v-list>
      <v-list-group v-if="!shouldShowOnlyModelReport && !isExternalUser && (showDataUpload || showModelingProcess)" prepend-icon="mdi-chart-timeline-variant-shimmer" :value=true no-action>
        <template v-slot:activator>
          <v-list-item-title id="model-setup-menu">{{ $t('views.App.modelSetup') }}</v-list-item-title>
        </template>
          <v-list-item v-if="showDataUpload" link :to="{path: '/dataUpload'}" :disabled="!currentClientLocal">
            <v-list-item-title>{{ $t('views.App.dataUpload') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-table-arrow-up</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="showDataUpload" link :to="{path: '/dataCheck'}" :disabled="!currentClientLocal">
            <v-list-item-title>{{ $t('views.App.dataCheck') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-table-eye</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="showModelingProcess" link :to="{path: '/modelingProcess'}" :disabled="!currentClientLocal">
            <v-list-item-title>{{ $t('views.App.modelingProcess') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-progress-check</v-icon>
            </v-list-item-icon>
          </v-list-item>
      </v-list-group>
      <v-list-group prepend-icon="mdi-file-chart" :value=true no-action>
        <template v-slot:activator>
          <v-list-item-title id="model-report-menu">{{ $t('views.App.modelReport') }}</v-list-item-title>
        </template>
          <v-list-item link :to="{name: 'modelReport'}" :disabled="!currentClientLocal || !currentModelHasConfig">
            <v-list-item-title id="model-overview-menu">{{ $t('views.App.modelOverview') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-chart-box</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="isAdHocWithReport" link :to="{name: 'whatIfScenario'}" :disabled="!currentClientLocal">
            <v-list-item-title id="what-if-scenario-menu">{{ $t('views.App.whatIfScenario') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-call-split</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="!shouldShowOnlyModelReport && !isExternalUser" link :to="{name: 'documents'}" :disabled="!currentClientLocal">
            <v-list-item-title id="files-menu">{{ $t('views.App.documents') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-file</v-icon>
            </v-list-item-icon>
          </v-list-item>
      </v-list-group>
      <v-list-group v-if="!shouldShowOnlyModelReport" prepend-icon="mdi-test-tube" :value=true no-action>
        <template v-slot:activator>
          <v-list-item-title id="media-planner-menu">{{ $t('views.App.mediaPlanner') }}</v-list-item-title>
        </template>
          <v-list-item link :to="{path: '/planSimulator'}" :disabled="!currentClientLocal || !currentModelHasConfig">
            <v-list-item-title>{{ $t('views.App.planCalculator') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-calculator</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/planOptimizer'}" :disabled="!currentClientLocal || !currentModelHasConfig">
            <v-list-item-title>{{ $t('views.App.planOptimizer') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-engine</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/budgetSimulator'}" :disabled="!currentClientLocal || !currentModelHasConfig">
            <v-list-item-title>{{ $t('views.App.budgetSimulator') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-chart-bell-curve-cumulative</v-icon>
            </v-list-item-icon>
          </v-list-item>
      </v-list-group>
      <v-list-group prepend-icon="mdi-lock" :value=false v-if="!shouldShowOnlyModelReport && (authUser.isAdmin || authUser.isScientist)" no-action>
        <template v-slot:activator>
          <v-list-item-title>{{ $t('views.App.admin') }}</v-list-item-title>
        </template>
          <v-list-item link :to="{path: '/adminUsers'}" v-if="authUser.isAdmin">
            <v-list-item-title>{{ $t('views.App.users') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-account-supervisor</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/adminReferenceModels'}">
            <v-list-item-title>{{ $t('views.App.refModels') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-stamper</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/adminAdHocModels'}">
            <v-list-item-title>{{ $t('views.App.adHocModelsMenu') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-graphql</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/adminCountries'}">
            <v-list-item-title>{{ $t('views.App.countries') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-flag-variant-outline</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="authUser.isAdmin" link :to="{path: '/adminNews'}">
            <v-list-item-title>{{ $t('views.App.news') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-newspaper</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="authUser.isAdmin" link :to="{path: '/adminPendingPlans'}">
            <v-list-item-title>{{ $t('views.App.pendingPlans') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-clock</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="authUser.isAdmin" link :to="{path: '/adminAIReviews'}">
            <v-list-item-title>{{ $t('views.App.aiReviews') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-robot</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item v-if="authUser.isAdmin" link :to="{path: '/adminErrorLogs'}">
            <v-list-item-title>{{ $t('views.App.errorLogs') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-bug</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item link :to="{path: '/stats'}" v-if="authUser.isAdmin">
            <v-list-item-title id="stats-menu">{{ $t('views.App.stats') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-google-analytics</v-icon>
            </v-list-item-icon>
          </v-list-item>
          <!-- Scientists only have the ngtorch override card in there, which exists only in dev. -->
          <v-list-item link :to="{path: '/adminTools'}" v-if="authUser.isAdmin || (authUser.isScientist && isDevEnvironment)">
            <v-list-item-title id="tools-menu">{{ $t('views.App.tools') }}</v-list-item-title>
            <v-list-item-icon>
              <v-icon>mdi-wrench</v-icon>
            </v-list-item-icon>
          </v-list-item>
      </v-list-group>
      <template v-slot:append>
        <!-- The two ways of making a model without a modelling engagement, under one head. Both
             children are reached from here only: the composer's own entry point used to be a button
             in the EKRM table, which is a different subject. The group is shown whenever either
             child is, which is the DIY audience — the composer is admin-only and every admin is in
             that set. -->
        <v-list v-if="!shouldShowOnlyModelReport && (authUser.isAdmin || authUser.isScientist || authUser.isManager || isDiyModeler)" class="py-0">
          <v-list-group prepend-icon="mdi-factory" :value=true no-action>
            <template v-slot:activator>
              <v-list-item-title id="model-factory-menu">{{ $t('views.App.modelFactory') }}</v-list-item-title>
            </template>
            <v-list-item link :to="{path: '/automaticModels'}">
              <v-list-item-title id="automatic-models-menu">{{ $t('views.App.automaticModeler') }}</v-list-item-title>
              <v-list-item-icon>
                <v-icon>mdi-puzzle-outline</v-icon>
              </v-list-item-icon>
            </v-list-item>
            <v-list-item link :to="{path: '/modelComposer'}" v-if="authUser.isAdmin">
              <v-list-item-title id="model-composer-menu">{{ $t('views.App.modelComposer') }}</v-list-item-title>
              <v-list-item-icon>
                <v-icon>mdi-layers-plus</v-icon>
              </v-list-item-icon>
            </v-list-item>
          </v-list-group>
        </v-list>
        <v-list-item link :to="{path: '/manageEkrm'}" v-if="!shouldShowOnlyModelReport && (authUser.isScientist || authUser.isManager || isEkrmManager)">
          <v-list-item-icon>
            <v-icon>mdi-head-snowflake</v-icon>
          </v-list-item-icon>
          <v-list-item-title id="ekrm-manager-menu">{{ $t('views.App.ekrmManager') }}</v-list-item-title>
        </v-list-item>
        <v-list-item link :to="{path: '/manageUserModels'}" v-if="!shouldShowOnlyModelReport && (authUser.isManager || authUser.isSupervisor)">
          <v-list-item-icon>
            <v-icon>mdi-account-supervisor</v-icon>
          </v-list-item-icon>
          <v-list-item-title id="user-manager-menu">{{ $t('views.App.userManager') }}</v-list-item-title>
        </v-list-item>
        <v-list-item link :to="{path: '/manageCustomModels'}" v-if="!shouldShowOnlyModelReport && (authUser.isManager || authUser.isCustomizer || isEkrmManager)">
          <v-list-item-icon>
            <v-icon>mdi-tune</v-icon>
          </v-list-item-icon>
          <v-list-item-title id="model-manager-menu">{{ $t('views.App.modelManager') }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="!shouldShowOnlyModelReport && !isExternalUser" link :to="{path: '/learningCenter'}">
          <v-list-item-icon>
            <v-icon>mdi-school</v-icon>
          </v-list-item-icon>
          <v-list-item-title id="larning-center-menu">{{ $t('views.App.learningCenter') }}</v-list-item-title>
        </v-list-item>
        <v-list-item link @click="logOut" v-if="!token && !iframe">
          <v-list-item-icon>
            <v-icon class="red--text">mdi-logout</v-icon>
          </v-list-item-icon>
          <v-list-item-title class="red--text">{{ $t('views.App.logout') }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-navigation-drawer>
    <!-- Before the app bar in the DOM, so Vuetify's layout engine stacks it above and shifts the
         bar and the main content down instead of overlapping them. Renders itself only while an
         ngtorch override is active, and deliberately is not gated on `plain`. -->
    <ngtorch-override-banner />
    <v-app-bar v-if="!plain && customView !== 'growthos'" app dark color="#212121">
      <v-app-bar-nav-icon @click.stop="drawer = !drawer"></v-app-bar-nav-icon>
      <!-- img v-if="!token && !iframe" :height="skin.headerLogoSize" :src="require(`@/assets/${skin.headerLogo}`)" :alt="skin.name" class="pl-2 pr-2" -->
      <img v-if="skin.headerLogo !== 'header_logo_predixon.png'" :height="skin.headerLogoSize" :src="require(`@/assets/${skin.headerLogo}`)" :alt="skin.name" class="pl-2 pr-2">
      <!--v-toolbar-title v-text="$route.name" class="pl-2"></v-toolbar-title-->
      <v-spacer></v-spacer>
      <v-icon class="mr-4" @click.stop="intro">mdi-help-circle-outline</v-icon>
      <v-btn icon v-if="recentNews.length > 0" @click.stop="showNewsPost(0)" class="mr-2">
        <v-badge :value="unreadNewsCount" color="red" :content="unreadNewsCount" overlap>
          <v-icon>mdi-newspaper</v-icon>
        </v-badge>
      </v-btn>
      <v-menu v-model="notificationsDrawer" :close-on-content-click="false" offset-y transition="slide-y-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="mr-2"
          >
          <v-badge :value="unreadNotificationsCount" color="red" :content="unreadNotificationsCount" overlap>
            <v-icon @click.stop="toggleNotifications">mdi-bell</v-icon>
          </v-badge>
          </v-btn>
        </template>
        <v-card class="notification-menu" dark>
          <v-list class="grey darken-4">
            <v-list-item-group v-if="notifications.length > 0">
              <span class="d-flex justify-end pr-2"><a style="text-decoration-line: underline;" @click="clearAllNotifications();notificationsDrawer = false">{{ $t('views.App.clearAllNotifications') }}</a></span>
              <v-list-item v-for="notification in notifications" :key="notification.id" class="notification-item" @click="viewNotification(notification)">
                <v-list-item-content>
                  <v-list-item-title :class="{ 'font-weight-bold': !notification.read }">
                    <v-icon v-if="notification.notificationType === 'document'" :color="notification.read ? 'grey' : 'primary'">mdi-file</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'simulation'" :color="notification.read ? 'grey' : 'primary'">mdi-test-tube</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'modelReport'" :color="notification.read ? 'grey' : 'primary'">mdi-file-chart</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'deliverable'" :color="notification.read ? 'grey' : 'primary'">mdi-file-document</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'userdataapproval'" :color="notification.read ? 'grey' : 'primary'">mdi-check</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'ekrm'" :color="notification.read ? 'grey' : 'primary'">mdi-head-snowflake</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'whatIfScenario'" :color="notification.read ? 'grey' : 'primary'">mdi-call-split</v-icon>
                    <v-icon v-else-if="notification.notificationType === 'budgetSimulation'" :color="notification.read ? 'grey' : 'primary'">mdi-chart-bell-curve-cumulative</v-icon>
                    <span class="pl-1" v-if="notification.notificationType === 'simulation'">
                      <b>{{ $t('views.App.mediaPlanReady') }}</b>
                    </span>
                    <span class="pl-1" v-else-if="notification.notificationType === 'modelReport'">
                      <b>{{ $t('views.App.modelReportReady') }}</b>
                    </span>
                    <span class="pl-1" v-else-if="notification.notificationType === 'ekrm'">
                      <b>{{ $t('views.App.ekrmReady') }}</b>
                    </span>
                    <span class="pl-1" v-else-if="notification.notificationType === 'whatIfScenario'">
                      <b>{{ $t('views.App.whatIfScenarioReady') }}</b>
                    </span>
                    <span class="pl-1" v-else-if="notification.notificationType === 'budgetSimulation'">
                      <b>{{ $t('views.App.budgetSimulationReady') }}</b>
                    </span>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption" v-if="notification.simulationName">
                    <b>{{ notification.simulationName }}</b>
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption" v-if="notification.clientId">
                    {{ clientIdToName(notification.clientId) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption" v-if="notification.ekrmName">
                    {{ notification.ekrmName }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="text-caption">
                    <i>{{ humanTime(notification.date_created) }}</i>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
            <v-list-item v-else>
              <v-list-item-content>
                <v-list-item-title class="text-center">{{ $t('views.App.noUnreadNotifications') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>
    <v-main>
      <router-view :key="currentClient" style="max-width: 1280px;"/>
      <v-container fluid>
        <v-layout align-center justify-center :style="!plain ? 'max-width: 1280px;' : ''">
          <v-flex xs12 sm8 md4>
            <div class="d-flex flex-column">
              <div style="text-align: center; padding-top: 40px;">
                <img v-if="skin.footerLogo" :height="skin.footerHeight" :src="require(`@/assets/${skin.footerLogo}`)">
              </div>
              <div style="text-align: center; padding-top: 10px;">
                <span class="overline">{{ skin.copyright }} <a href="https://www.noisegrasp.com" target="_blank">NoiseGrasp</a>©</span>
                <div class="overline"><a href="#" @click="showTermsDialog = true">{{ $t('views.App.termsAndConditions') }}</a></div>
                <div class="text--disabled caption">{{ $gitHash ? $gitHash : '.' }}</div>
                <div class="text--disabled" style="font-size: xx-small;">[{{ authUser.email }}]</div>
                <div style="margin-top: 8px;">
                  <v-select
                    v-model="selectedLanguage"
                    :items="languages"
                    item-text="name"
                    item-value="code"
                    dense
                    outlined
                    hide-details
                    style="max-width: 120px; margin: 0 auto;"
                    class="caption"
                  ></v-select>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
  </v-app>
  <v-app v-else-if="shouldRenderMainApp">
    <v-main v-if="token || oid || oidAuthorize">
      <v-container fluid fill-height>
        <v-layout align-center justify-center>
          <v-flex xs12 sm8 md4>
            <div class="d-flex flex-column">
              <div v-if="tokenError">
                {{ tokenError }}
              </div>
              <div v-else-if="oidError">
                {{ oidError }}
              </div>
              <div v-else>
                <div>
                  <v-progress-circular indeterminate color="primary" style="margin-right: 15px;"></v-progress-circular>{{ $t('views.App.authenticatingUser') }}
                </div>
                <a v-if="redirectUrl" @click="openPopup">{{ $t('views.App.clickToSignIn') }}</a>
              </div>
              <div class="text--disabled caption">{{ $gitHash ? $gitHash : '.' }}</div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
    <v-main v-else-if="!iframe && !token">
      <v-container fluid fill-height>
        <v-layout align-center justify-center>
          <v-flex xs12 sm8 md4>
            <div class="d-flex flex-column">
              <div style="text-align: center; padding-bottom: 30px;">
                <img height="100px" :src="require(`@/assets/${skin.loginLogo}`)" :alt="skin.name">
              </div>
              <v-card class="elevation-12">
                <v-toolbar dark color="primary">
                  <v-toolbar-title>{{ $t('views.App.login') }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <v-form>
                    <v-text-field prepend-icon="mdi-account" name="email" :label="$t('views.App.email')" type="text" v-model="logInEmail" v-on:keyup.enter="logIn" @input="logInError = ''" :disabled="tryingLogIn"></v-text-field>
                    <v-text-field id="password" prepend-icon="mdi-lock" name="password" :label="$t('views.App.password')" type="password" v-model="logInPassword" v-on:keyup.enter="logIn" @input="logInError = ''" :disabled="tryingLogIn"></v-text-field>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn v-if="!logInEmail.includes('@noisegrasp.com')" color="primary" @click="logIn" :disabled="tryingLogIn">
                    <span v-if="!tryingLogIn">{{ $t('views.App.login') }}</span>
                    <span v-else><v-progress-circular indeterminate color="primary"></v-progress-circular></span>
                  </v-btn>
                  <v-btn v-else color="primary" @click="logInWithGoogle" :disabled="tryingLogIn">
                    <span v-if="!tryingLogIn">{{ $t('views.App.loginWithGoogle') }}</span>
                    <span v-else><v-progress-circular indeterminate color="primary"></v-progress-circular></span>
                  </v-btn>
                </v-card-actions>
              </v-card>
              <div style="text-align: center; padding-top: 20px;">
                <v-dialog v-model="dialogResetPassword" hide-overlay width="500">
                  <template v-slot:activator="{ on: dialog }">
                    <a href="#" v-on="{...dialog}">{{ $t('views.App.forgotPassword') }}</a>
                    <v-snackbar
                      v-model="snackbarResetPassword"
                      :timeout="-1"
                      :value="true"
                      elevation="24"
                      color="primary"
                      text
                    >
                      {{ $t('views.App.checkEmailForReset') }}
                      <template v-slot:action="{ attrs }">
                        <v-btn
                          color="primary"
                          text
                          v-bind="attrs"
                          @click="snackbarResetPassword = false"
                        >
                          {{ $t('views.App.ok') }}
                        </v-btn>
                      </template>
                    </v-snackbar>
                  </template>
                    <v-card>
                      <v-card-title
                        class="text-h5"
                        primary-title
                      >
                        {{ $t('views.App.resetPassword') }}
                      </v-card-title>
                      <v-card-text>
                        <v-text-field
                          v-model="logInEmail"
                          :label="$t('views.App.enterYourEmail')"
                          required
                          autofocus
                        ></v-text-field>
                      </v-card-text>
                      <v-divider></v-divider>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="warning"
                          text
                          @click="dialogResetPassword = false"
                        >
                          {{ $t('views.App.cancel') }}
                        </v-btn>
                        <v-btn
                          :disabled="logInEmail.length === 0"
                          color="primary"
                          text
                          @click="dialogResetPassword = false; resetEmail()"
                        >
                          {{ $t('views.App.sendResetEmail') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                </v-dialog>
              </div>
              <v-alert v-if="logInError" :value="true" type="error">
                {{ logInError }}
              </v-alert>
              <div style="text-align: center; padding-top: 40px;">
                <img v-if="skin.footerLogo" :height="skin.footerHeight" :src="require(`@/assets/${skin.footerLogo}`)">
              </div>
              <div style="text-align: center; padding-top: 10px;">
                <span class="overline">{{ skin.copyright }} <a href="https://www.noisegrasp.com" target="_blank">NoiseGrasp</a>©</span>
                <div class="overline"><a href="#" @click="showTermsDialog = true">{{ $t('views.App.termsAndConditions') }}</a></div>
                <div class="text--disabled caption">{{ $gitHash ? $gitHash : '.' }}</div>
                <div style="margin-top: 8px;">
                  <v-select
                    v-model="selectedLanguage"
                    :items="languages"
                    item-text="name"
                    item-value="code"
                    dense
                    outlined
                    hide-details
                    style="max-width: 120px; margin: 0 auto;"
                    class="caption"
                  ></v-select>
                </div>
              </div>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-main>
  </v-app>
  <v-app v-else>
    <v-main>
      <v-progress-linear :indeterminate="true"></v-progress-linear>
      <div class="ml-4 mt-4 mb-2">{{ $t('views.App.loading') }}</div>
      <div class="ml-4 text--disabled caption">{{ $gitHash ? $gitHash : '.' }}</div>
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { driver } from '@/mixins/driver.js'

import moment from 'moment'
import _ from 'lodash'
import { changeLanguage, loadLanguageFromStorage } from '@/i18n/languageSync'
import NgtorchOverrideBanner from '@/components/NgtorchOverrideBanner'
import ModelVersionBadge from '@/components/ModelVersionBadge'
import firebase from '@/firebase'
import {
  countFamilyRows, familyRowsPage, filterFamilyRows, lastFamilyPage, latestInFamily, sortFamilyRows,
  versionFamilyId, versionNumber
} from '@/utils/modelVersions.js'

export default {
  name: 'App',
  components: { NgtorchOverrideBanner, ModelVersionBadge },
  mixins: [driver],
  data: () => ({
    isParamsLoaded: false,
    // Ad-Hoc version families expanded in the Model Selection table
    expandedVersionFamilies: [],
    snackbarResetPassword: false,
    dialogResetPassword: false,
    newPassword: '',
    confirmNewPassword: '',
    drawer: null,
    logInEmail: '',
    logInPassword: '',
    logInError: '',
    tryingLogIn: false,
    tryingSetNewPassword: false,
    tokenError: null,
    oidError: null,
    modelDialog: false,
    typeModelDialog: 0,
    modelDialogSearch: '',
    modelDialogSortBy: ['date_created_formatted'],
    modelDialogSortDesc: [true],
    modelDialogPage: 1,
    windowHeight: window.innerHeight,
    selectedCountries: [],
    countrySearch: '',
    countryFilterMenu: false,
    selectedBrands: [],
    brandSearch: '',
    brandFilterMenu: false,
    metadataFilterConfigs: [
      { key: 'client', valueField: 'clientName', icon: 'mdi-domain', headerKey: 'headerClient', filterKey: 'filterByClient', searchKey: 'searchClients', allKey: 'allClients', selectedKey: 'clientsSelected' },
      { key: 'category', valueField: 'categoryName', icon: 'mdi-shape', headerKey: 'headerCategory', filterKey: 'filterByCategory', searchKey: 'searchCategories', allKey: 'allCategories', selectedKey: 'categoriesSelected' },
      { key: 'sub-category', valueField: 'subcategoryName', icon: 'mdi-shape-outline', headerKey: 'headerSubcategory', filterKey: 'filterBySubcategory', searchKey: 'searchSubcategories', allKey: 'allSubcategories', selectedKey: 'subcategoriesSelected' }
    ],
    metadataSelected: { client: [], category: [], 'sub-category': [] },
    metadataSearch: { client: '', category: '', 'sub-category': '' },
    metadataMenu: { client: false, category: false, 'sub-category': false },
    currentPassword: '',
    newPassword2: '',
    showCurrentPassword: false,
    showNewPassword: false,
    wrongCurrentPassword: false,
    updatedPassword: false,
    forgotPasswordMessage: false,
    notificationsDrawer: false,
    showNewsDialog: false,
    showTermsDialog: false,
    currentNewsIndex: 0,
    acceptingTerms: false,
    languages: [
      { code: 'en', name: 'English' },
      { code: 'es', name: 'Español' },
      { code: 'pt', name: 'Português' }
    ]
  }),
  methods: {
    ...mapMutations('clients', ['setClientsInitialized']),
    ...mapActions('clients', [
      'loadClients',
      'loadClientsRealtime',
      'setCurrentClient',
      'loadAllClients',
      'setBasePrimaryColor',
      'loadGroupClients',
      'loadGroupClientsRealtime'
    ]),
    ...mapActions('auth', [
      'initAuth',
      'signIn',
      'signOut',
      'signInWithToken',
      'signInWithOid',
      'authorizeWithOid',
      'resetPassword',
      'signInWithGoogle',
      'confirmPasswordReset',
      'setNewPasswordByUser',
      'markNotificationAsViewed',
      'clearAllNotifications',
      'markNewsPostAsRead',
      'sendAuthToOpener',
      'acceptTermsAndConditions'
    ]),
    ...mapActions('settings', [
      'loadCountries'
    ]),
    ...mapActions('simulations', [
      'loadSimulations'
    ]),
    openPopup () {
      window.open(this.redirectUrl, 'LionLogin', 'width=600,height=400')
      this.initAuth()
    },
    acceptTerms () {
      this.acceptingTerms = true
      this.acceptTermsAndConditions()
        .then(() => {
          this.acceptingTerms = false
          this.showTermsDialog = false
        })
        .catch((error) => {
          this.acceptingTerms = false
          console.error('Error accepting terms:', error)
          // You might want to show an error message to the user here
        })
    },
    switchLanguage (lang) {
      changeLanguage(this.$i18n, this.$store, lang)
    },
    formattedContent (content) {
      return content ? content.replace(/\n/g, '<br>') : ''
    },
    showNewsPost (index) {
      this.currentNewsIndex = index
      this.showNewsDialog = true
      if (this.recentNews[index]) {
        this.markNewsPostAsRead(this.recentNews[index].id) // Mark as read when opened
      }
    },
    viewNotification (notification) {
      this.notificationsDrawer = false
      if (notification.notificationType === 'simulation') {
        this.$router.push({ name: 'simulationDetails', params: { simulation: notification.simulationId, client: notification.clientId }, hash: `${notification.id}` }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Handle other errors
            throw err
          }
        })
      } else if (notification.notificationType === 'modelReport') {
        this.$router.push({ name: 'modelReport', params: { client: notification.clientId }, hash: `${notification.id}` }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Handle other errors
            throw err
          }
        })
      } else if (notification.notificationType === 'ekrm') {
        this.$router.push({ name: 'manageEkrm', hash: `${notification.id}` }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Handle other errors
            throw err
          }
        })
      } else if (notification.notificationType === 'whatIfScenario') {
        this.$router.push({ name: 'whatIfScenario', params: { client: notification.clientId }, hash: `${notification.id}` }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Handle other errors
            throw err
          }
        })
      } else if (notification.notificationType === 'budgetSimulation') {
        this.$router.push({ name: 'budgetSimulationDetails', params: { id: notification.simulationId, client: notification.clientId }, hash: `${notification.id}` }).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Handle other errors
            throw err
          }
        })
      }
      this.markNotificationAsViewed({
        notificationId: notification.id,
        uid: this.authUser.uid
      })
    },
    clientIdToName (id) {
      if (this.userClients) {
        const client = this.userClients.find(obj => obj.id === id)
        if (client && client.name) {
          return client.name
        } else {
          return ''
        }
      } else {
        return ''
      }
    },
    highlightText (text, searchQuery) {
      if (!searchQuery || !text) {
        return text
      }

      // Escape special regex characters in the search query
      const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

      // Create case-insensitive regex
      const regex = new RegExp(`(${escapedQuery})`, 'gi')

      // Replace matches with highlighted version
      return text.replace(regex, '<span class="search-highlight">$1</span>')
    },
    getNestedValue (obj, path) {
      return _.get(obj, path, '')
    },
    toggleSelectAll () {
      const visibleCountries = this.availableCountries.filter(c =>
        !this.countrySearch || c.text.toLowerCase().includes(this.countrySearch.toLowerCase())
      )
      const visibleValues = visibleCountries.map(c => c.value)
      const allVisibleSelected = visibleValues.every(v => this.selectedCountries.includes(v))

      if (allVisibleSelected) {
        // Unselect all visible countries
        this.selectedCountries = this.selectedCountries.filter(v => !visibleValues.includes(v))
      } else {
        // Select all visible countries (add to existing selection)
        const newSelection = new Set([...this.selectedCountries, ...visibleValues])
        this.selectedCountries = Array.from(newSelection)
      }
    },
    toggleCountry (countryValue) {
      if (this.selectedCountries.includes(countryValue)) {
        this.selectedCountries = this.selectedCountries.filter(c => c !== countryValue)
      } else {
        this.selectedCountries = [...this.selectedCountries, countryValue]
      }
    },
    toggleBrand (brandValue) {
      if (this.selectedBrands.includes(brandValue)) {
        this.selectedBrands = this.selectedBrands.filter(b => b !== brandValue)
      } else {
        this.selectedBrands = [...this.selectedBrands, brandValue]
      }
    },
    toggleSelectAllBrands () {
      const visibleBrands = this.availableBrands.filter(b =>
        !this.brandSearch || b.text.toLowerCase().includes(this.brandSearch.toLowerCase())
      )
      const visibleValues = visibleBrands.map(b => b.value)
      const allVisibleSelected = visibleValues.every(v => this.selectedBrands.includes(v))

      if (allVisibleSelected) {
        // Unselect all visible brands
        this.selectedBrands = this.selectedBrands.filter(v => !visibleValues.includes(v))
      } else {
        // Select all visible brands (add to existing selection)
        const newSelection = new Set([...this.selectedBrands, ...visibleValues])
        this.selectedBrands = Array.from(newSelection)
      }
    },
    humanTime (timestamp) {
      const now = Date.now()
      const diffInSeconds = Math.floor((now - timestamp) / 1000)
      const intervals = [
        { key: 'yearsAgo', seconds: 31536000 },
        { key: 'monthsAgo', seconds: 2592000 },
        { key: 'daysAgo', seconds: 86400 },
        { key: 'hoursAgo', seconds: 3600 },
        { key: 'minutesAgo', seconds: 60 },
        { key: 'secondsAgo', seconds: 1 }
      ]
      for (let i = 0; i < intervals.length; i++) {
        const interval = intervals[i]
        const count = Math.floor(diffInSeconds / interval.seconds)
        if (count >= 1) {
          return this.$tc('views.App.' + interval.key, count, { count: count })
        }
      }
      return this.$t('views.App.justNow')
    },
    toggleNotifications () {
      this.notificationsDrawer = !this.notificationsDrawer
    },
    forgotPassword () {
      this.resetPassword({ email: this.authUser.email }).catch(function () {})
      this.forgotPasswordMessage = true
    },
    // This method sets a new password for the dialog shown when first logging in with an automatically generated password
    setNewPasswordFromDialog () {
      // Set new password
      this.setNewPasswordByUser({
        email: this.authUser.email,
        currentPassword: this.currentPassword,
        newPassword: this.newPassword
      }).then(() => {
        // Reset values
        this.currentPassword = ''
        this.newPassword = ''
        this.newPassword2 = ''
        this.wrongCurrentPassword = false
        // TODO: TRy to do this with an auto-udate from the db
        this.updatedPassword = true
      }).catch(error => {
        // Wrong current password error handling
        if (error.code === 'auth/invalid-login-credentials') {
          this.wrongCurrentPassword = true
        } else {
          alert(this.$t('views.App.internalError') + error.code)
        }
      })
    },
    // This method sets a new password for the "Forgot Password" workflow
    setNewPassword () {
      this.confirmPasswordReset({
        actionCode: this.actionCode,
        newPassword: this.newPassword
      }).then(() => {
        // Sing-in
        console.log('Password set successfully')
        alert(this.$t('views.App.passwordSetSuccessfully'))
        document.location.href = '/'
      }).catch((error) => {
        // Error occurred during confirmation. The code might have expired or the
        // password is too weak.
        console.log(error)
        this.logInError = error.message
      })
    },
    resetEmail () {
      this.resetPassword({ email: this.logInEmail }).catch(function () {})
      this.snackbarResetPassword = true
    },
    logInWithGoogle () {
      // Disable login form
      this.tryingLogIn = true
      this.signInWithGoogle().catch((error) => {
        this.logInError = error.message
        this.logInPassword = ''
        this.tryingLogIn = false
      })
    },
    logIn () {
      // Disable login form
      this.tryingLogIn = true
      this.signIn({
        user: this.logInEmail,
        password: this.logInPassword
      }).catch((error) => {
        this.logInError = error.message
        this.logInPassword = ''
        this.tryingLogIn = false
      })
    },
    logOut () {
      this.signOut()
    },
    intro () {
      this.drawer = true
      var steps = [{
        element: '#current-model-menu',
        popover: {
          title: this.$t('views.App.introCurrentModelTitle'),
          description: this.$t('views.App.introCurrentModelDesc'),
          position: 'right'
        },
        padding: 0
      },
      {
        element: '#model-report-menu',
        popover: {
          title: this.$t('views.App.introModelReportTitle'),
          description: this.$t('views.App.introModelReportDesc'),
          position: 'right'
        }
      },
      {
        element: '#media-planner-menu',
        popover: {
          title: this.$t('views.App.introMediaPlannerTitle'),
          description: this.$t('views.App.introMediaPlannerDesc'),
          position: 'right'
        }
      }]
      if (this.authUser.isManager || this.authUser.isAdmin || this.authUser.isScientist) {
        steps.unshift({
          element: '#model-manager-menu',
          popover: {
            title: this.$t('views.App.introModelManagerTitle'),
            description: this.$t('views.App.introModelManagerDesc'),
            position: 'top'
          }
        })
      }
      this.driver.defineSteps(steps)
      this.driver.start()
    },
    noModelSelected () {
      var steps = [{
        element: '#current-model-menu',
        popover: {
          title: this.$t('views.App.introCurrentModelTitle'),
          description: this.$t('views.App.introCurrentModelDesc'),
          position: 'right'
        },
        padding: 0
      }]
      this.driver.defineSteps(steps)
      this.driver.start()
    },
    currentModelDeleted () {
      var steps = [{
        element: '#current-model-menu',
        popover: {
          title: this.$t('views.App.introModelDeletedTitle'),
          description: this.$t('views.App.introModelDeletedDesc'),
          position: 'right'
        },
        padding: 0
      }]
      this.driver.defineSteps(steps)
      this.driver.start()
    },
    dateModified (item) {
      const date = item.date_edited ? item.date_edited : item.date_created
      if (date) {
        return moment(date.toDate()).format('YYYY/MM/DD HH:mm')
      } else {
        return null
      }
    },
    versionNumber,
    toggleVersionFamily (item) {
      const familyId = versionFamilyId(item)
      this.expandedVersionFamilies = this.expandedVersionFamilies.includes(familyId)
        ? this.expandedVersionFamilies.filter(id => id !== familyId)
        : this.expandedVersionFamilies.concat(familyId)
    },
    isVersionFamilyExpanded (item) {
      return this.expandedVersionFamilies.includes(versionFamilyId(item))
    },
    // Vuetify's default table search: some column's value contains the search term
    matchesModelDialogSearch (item, search) {
      return this.modelDialogheaders.some(header => {
        const value = _.get(item, header.value)
        return value != null && typeof value !== 'boolean' &&
          value.toString().toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
      })
    },
    modelDialogItemClass (item) {
      return item.isOlderVersion ? 'model-version-row' : ''
    },
    isLatestVersion (model) {
      const versions = this.modelVersionFamilies.get(versionFamilyId(model))
      return !versions || latestInFamily(versions).id === model.id
    },
    // date_created: in production importModel creates the doc on deploy, so it is the deploy date.
    versionDate (model) {
      return model.date_created ? moment(model.date_created.toDate()).format('YYYY/MM/DD HH:mm') : ''
    },
    dateModifiedDate (item) {
      const date = item.date_edited ? item.date_edited : item.date_created
      return date ? moment(date.toDate()).format('YYYY/MM/DD') : null
    },
    dateModifiedTime (item) {
      const date = item.date_edited ? item.date_edited : item.date_created
      return date ? moment(date.toDate()).format('HH:mm') : ''
    },
    sortModelDialogRows (items, sortBy, sortDesc) {
      var sortCol = sortBy[0]
      if (sortCol === 'date_created_formatted') {
        items.sort(function (a, b) {
          var aDate = a.date_created
          var bDate = b.date_created
          if (a.date_edited) {
            aDate = a.date_edited
          }
          if (b.date_edited) {
            bDate = b.date_edited
          }
          return sortDesc[0] ? bDate - aDate : aDate - bDate
        })
      } else {
        items.sort(function (a, b) {
          var aVal = (a[sortCol] || '').toString()
          var bVal = (b[sortCol] || '').toString()
          return sortDesc[0]
            ? bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' })
            : aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
        })
      }
      return items
    },
    getCountryName (countryCode) {
      const countryObj = this.countries.find(c => c.id === countryCode)
      return countryObj ? countryObj.name : countryCode || 'N/A'
    },
    resolveCountryKey (value) {
      // Collapses the same country expressed two ways (a registry code like "MX"
      // from old model data vs. a name like "Mexico" from client_metadata) into a
      // single canonical key so the country filter doesn't list it twice.
      if (value === null || value === undefined) {
        return null
      }
      const str = value.toString().trim()
      if (str === '') {
        return null
      }
      // Already a registry code (id)
      if (this.countries.some(c => c.id === str)) {
        return str
      }
      // Matches a registry name (case-insensitive) -> use its id so a name and
      // its code collapse to the same key
      const byName = this.countries.find(c => c.name && c.name.toLowerCase() === str.toLowerCase())
      if (byName) {
        return byName.id
      }
      // Unknown free-text country: key on the lowercased value so casing variants merge
      return str.toLowerCase()
    },
    getAdHocCountryName (item) {
      const countryCode = this.getAdHocCountryCode(item)
      return countryCode ? this.getCountryName(countryCode) : 'N/A'
    },
    getAdHocCountryCode (item) {
      // Prefer the curated client_metadata.country when set
      if (item.client_metadata && item.client_metadata.country) {
        return item.client_metadata.country
      }
      // Fall back to the first brand's country in reference_model.config.brands
      if (item.reference_model && item.reference_model.config && item.reference_model.config.brands) {
        // Get the first brand's country (all brands have the same country)
        const firstBrandKey = Object.keys(item.reference_model.config.brands)[0]
        if (firstBrandKey && item.reference_model.config.brands[firstBrandKey]) {
          return item.reference_model.config.brands[firstBrandKey].country
        }
      }
      return null
    },
    getCustomCountryCode (item) {
      // Prefer the curated client_metadata.country when set
      if (item.client_metadata && item.client_metadata.country) {
        return item.client_metadata.country
      }
      if (item.custom_model && item.custom_model.country) {
        return item.custom_model.country
      }
      return null
    },
    getModelBrands (item) {
      // Prefer the curated client_metadata.brand when set
      if (item.client_metadata && item.client_metadata.brand) {
        return [item.client_metadata.brand]
      }
      // Fall back to the model's reference_model.config.brands keys
      if (item.reference_model && item.reference_model.config && item.reference_model.config.brands) {
        return Object.keys(item.reference_model.config.brands)
      }
      return []
    },
    getAdHocBrands (item) {
      const brands = this.getModelBrands(item)
      return brands.length > 0 ? brands.join(', ') : 'N/A'
    },
    getMetadataValue (item, key) {
      // Curated client_metadata value for the given key (no fallback, unlike country/brand)
      return (item.client_metadata && item.client_metadata[key]) ? item.client_metadata[key] : null
    },
    isNaValue (val) {
      // Empty fields ('') and the literal 'N/A' sentinel from getAdHocBrands/getAdHocCountryName
      // are both treated as "not available".
      return !val || val === 'N/A'
    },
    selectModel (item) {
      this.modelDialog = false
      this.currentClientLocal = item.id
    },
    availableMetadata (key) {
      // Unique values for the metadata key scoped to the ad-hoc tab, so the filter
      // only offers values that actually appear among the models currently listed.
      const values = new Set()
      this.userClients.filter(client => client.type === 'mmm').forEach(item => {
        const val = this.getMetadataValue(item, key)
        if (val !== null) {
          values.add(val)
        }
      })
      return Array.from(values).sort((a, b) => a.localeCompare(b)).map(val => ({
        value: val,
        text: val
      }))
    },
    filteredMetadata (key) {
      const search = this.metadataSearch[key]
      if (!search) {
        return this.availableMetadata(key)
      }
      return this.availableMetadata(key).filter(opt =>
        opt.text.toLowerCase().includes(search.toLowerCase())
      )
    },
    toggleMetadata (key, value) {
      if (this.metadataSelected[key].includes(value)) {
        this.metadataSelected[key] = this.metadataSelected[key].filter(v => v !== value)
      } else {
        this.metadataSelected[key] = [...this.metadataSelected[key], value]
      }
    },
    toggleSelectAllMetadata (key) {
      const search = this.metadataSearch[key]
      const visible = this.availableMetadata(key).filter(opt =>
        !search || opt.text.toLowerCase().includes(search.toLowerCase())
      )
      const visibleValues = visible.map(opt => opt.value)
      const allVisibleSelected = visibleValues.every(v => this.metadataSelected[key].includes(v))

      if (allVisibleSelected) {
        this.metadataSelected[key] = this.metadataSelected[key].filter(v => !visibleValues.includes(v))
      } else {
        const newSelection = new Set([...this.metadataSelected[key], ...visibleValues])
        this.metadataSelected[key] = Array.from(newSelection)
      }
    },
    hasActiveMetadataFilter (key) {
      // Returns true when some (but not all) values are selected
      const total = this.availableMetadata(key).length
      const selectedCount = this.metadataSelected[key].length
      return selectedCount > 0 && selectedCount < total
    },
    selectAllMetadataIcon (key) {
      const search = this.metadataSearch[key]
      const visibleValues = this.availableMetadata(key)
        .filter(opt => !search || opt.text.toLowerCase().includes(search.toLowerCase()))
        .map(opt => opt.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.metadataSelected[key].includes(v))
      const someVisibleSelected = visibleValues.some(v => this.metadataSelected[key].includes(v))

      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllMetadataText (key) {
      const search = this.metadataSearch[key]
      const visible = this.availableMetadata(key)
        .filter(opt => !search || opt.text.toLowerCase().includes(search.toLowerCase()))
      if (search && visible.length > 0) {
        return this.$t('views.App.selectAllVisible', { count: visible.length })
      }
      return this.$t('views.App.selectAll')
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser',
      'notifications',
      'news',
      'redirectUrl',
      'isEkrmManager',
      'isDataUploader',
      'isDiyModeler',
      'shouldShowOnlyModelReport',
      'isExternalUser'
    ]),
    // Same check as adminTools' own, read off the app the page is connected to.
    isDevEnvironment () {
      return firebase.db.app.options.projectId === 'ngapp5-dev'
    },
    ...mapGetters('settings', [
      'countries'
    ]),
    ...mapGetters('clients', [
      'clients',
      'groupClients',
      'currentClient',
      'currentClientObject',
      'basePrimaryColor',
      'clientsInitialized',
      'modelVersionFamilies'
    ]),
    // The What-If Scenario menu entry shows for any Ad-Hoc model that has a Model Report. The
    // strategy-check plans it relies on may not exist yet — the view itself offers a button to
    // create them on demand — so visibility is no longer gated on the plans already existing.
    // A model has no config for a while after it is created. Model Overview, Plan Calculator
    // and Plan Optimizer need it, so their menu entries are disabled until it is set.
    currentModelHasConfig () {
      const c = this.currentClientObject
      return !!(c && c.reference_model && c.reference_model.config)
    },
    // True when the loaded current model has no config but the route is one of those sections,
    // which the disabled menu entries alone don't prevent: the model selector keeps the current
    // route, and a reload or a link restores it together with the stored model.
    onConfigRouteWithoutConfig () {
      const c = this.currentClientObject
      return !!(c && c.id === this.currentClient && !this.currentModelHasConfig &&
        ['modelReport', 'planSimulator', 'planOptimizer'].includes(this.$route.name))
    },
    isAdHocWithReport () {
      const c = this.currentClientObject
      return !!(c && c.type === 'mmm' && (c.model_report || c.model_report_storage))
    },
    selectedLanguage: {
      get () {
        return this.$i18n.locale
      },
      set (lang) {
        this.switchLanguage(lang)
      }
    },
    shouldRenderMainApp () {
      const baseConditions = this.authUser.initialized && this.isParamsLoaded && this.clientsInitialized

      // If user had a model selected, wait for it to be fully loaded before showing UI
      if (localStorage.currentClientId) {
        return baseConditions && this.currentClientObject && this.currentClientObject.id === localStorage.currentClientId
      }

      return baseConditions
    },
    recentNews () {
      const thirtyDaysAgo = moment().subtract(30, 'days')
      return this.news.filter(post => {
        return moment(post.dateCreated.toDate()).isAfter(thirtyDaysAgo)
      }).reverse()
    },
    unreadNewsCount () {
      return this.recentNews.filter(post => this.authUser.readNewsPosts && !this.authUser.readNewsPosts.includes(post.id)).length
    },
    unreadNotificationsCount () {
      return this.notifications.filter(n => !n.viewed).length
    },
    re () {
      return new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^*-]).{12,}$')
    },
    currentPasswordRules () {
      return [
        value => !!value || this.$t('views.App.required'),
        () => !this.wrongCurrentPassword || this.$t('views.App.wrongPassword')
      ]
    },
    newPasswordRules () {
      return [
        value => !!value || this.$t('views.App.required'),
        () => this.newPassword === this.newPassword2 || this.$t('views.App.passwordsMustMatch'),
        () => this.newPassword !== this.currentPassword || this.$t('views.App.passwordMustBeDifferent'),
        v => this.re.test(v) || this.$t('views.App.passwordRequirements')
      ]
    },
    resetPasswordDialog () {
      console.log(this.authUser)
      if (this.updatedPassword || this.authUser.password || this.authUser.password === null || this.authUser.password === undefined || this.authUser.email.endsWith('@noisegrasp.com')) {
        return false
      } else {
        return true
      }
    },
    showDataUpload () {
      if (this.authUser.isAdmin || this.authUser.isScientist || this.authUser.isManager || this.isDataUploader) {
        if (this.currentClientObject) {
          if (this.currentClientObject.enableDataUploader) {
            return true
          }
        }
      }
      return false
    },
    showModelingProcess () {
      if (this.currentClientObject && this.currentClientObject.enabledStages) {
        if (Object.values(this.currentClientObject.enabledStages).some(value => value === true)) {
          return true
        }
      }
      return false
    },
    actionCode () {
      return this.$route.query.oobCode
    },
    invalidConfirmNewPassword () {
      return this.newPassword !== this.confirmNewPassword
    },
    invalidNewPassword () {
      const re = new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^*-]).{12,}$')
      if (!re.test(this.newPassword)) {
        return true
      } else {
        return false
      }
    },
    resetPasswordForm () {
      if (this.$route.query.mode && this.$route.query.mode === 'resetPassword' && this.$route.query.oobCode) {
        return true
      } else {
        return false
      }
    },
    userClients () {
      return _.uniqBy(_.union(this.clients, this.groupClients), 'id').sort((a, b) => {
        return a.name.localeCompare(b.name)
      })
    },
    // The live doc of the selected model (currentClientObject is a copy taken when it was selected).
    currentModelDoc () {
      return this.userClients.find(client => client.id === this.currentClient) || null
    },
    hostname () {
      return window.location.hostname.substring(0, window.location.hostname.indexOf('.'))
    },
    skin () {
      // Duplicated in App.vue, Home.vue, and for model_status functionality
      // Also present in SharedPlan.vue
      // TODO: Unify
      if (window.location.hostname === 'predixon.noisegrasp.com') {
        return {
          name: 'Predixon',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_predixon.png',
          footerLogo: 'footer_logo_predixon.png',
          loginLogo: 'login_logo_predixon.png',
          headerLogoSize: '45px',
          footerHeight: '100px',
          color: '#212121',
          dark: true
        }
      } else if (window.location.hostname === 'outflink.noisegrasp.com') {
        return {
          name: 'Outflink',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_outflink.png',
          footerLogo: 'footer_logo_outflink.png',
          loginLogo: 'login_logo_outflink.png',
          headerLogoSize: '25px',
          footerHeight: '50px',
          color: '#212121',
          dark: true
        }
      } else if (window.location.hostname === 'deloitte.noisegrasp.com') {
        return {
          name: 'Deloitte Marketing Mix Modeling',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_deloitte.png',
          footerLogo: 'footer_logo_deloitte.png',
          loginLogo: 'login_logo_deloitte.png',
          headerLogoSize: '50px',
          footerHeight: '100px',
          color: '#212121',
          dark: true
        }
      } else if (window.location.hostname === 'nisum.noisegrasp.com') {
        return {
          name: 'Nisum Marketing Mix Modeling',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_nisum.webp',
          footerLogo: 'footer_logo_nisum.webp',
          loginLogo: 'login_logo_nisum.webp',
          headerLogoSize: '50px',
          footerHeight: '100px',
          color: '#212121',
          dark: true
        }
      } else if (window.location.hostname === 'mckinsey.noisegrasp.com') {
        return {
          name: 'McKinsey Marketing Mix Modeling',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_mckinsey.png',
          footerLogo: 'footer_logo_mckinsey.png',
          loginLogo: 'login_logo_mckinsey.png',
          headerLogoSize: '50px',
          footerHeight: '100px',
          color: '#212121',
          dark: true
        }
      } else if (window.location.hostname === 'comscore.noisegrasp.com') {
        return {
          name: 'Comscore Marketing Mix Modeling',
          copyright: this.$t('views.App.poweredBy'),
          headerLogo: 'header_logo_comscore.png',
          footerLogo: 'footer_logo_comscore.png',
          loginLogo: 'login_logo_comscore.png',
          headerLogoSize: '50px',
          footerHeight: '100px',
          color: '#212121',
          dark: true
        }
      } else {
        return {
          name: 'NoiseGrasp',
          copyright: this.$t('views.App.allRightsReserved'),
          headerLogo: 'header_logo.png',
          footerLogo: 'logo-ng.png',
          loginLogo: 'login_logo.png',
          headerLogoSize: '25px',
          footerHeight: '50px',
          color: '#212121',
          dark: true
        }
      }
    },
    visibleMetadataConfigs () {
      // Only the metadata filters/columns that have at least one value across the
      // whole ad-hoc pool are surfaced.
      return this.metadataFilterConfigs.filter(cfg => this.availableMetadata(cfg.key).length > 0)
    },
    modelDialogTableHeight () {
      // The dialog can grow to ~90vh; reserve space for its chrome (title + filter row,
      // tabs, footer, close button, paddings) so the table fills the rest of the viewport.
      const reserved = 320
      return Math.max(200, Math.round(this.windowHeight * 0.9) - reserved)
    },
    modelDialogRows () {
      // How many compact rows fit in the available table height, so the page never needs
      // an inner scroll. Row/header heights match the .compact-model-table CSS.
      const rowHeight = 28
      const headerHeight = 36
      return Math.max(5, Math.floor((this.modelDialogTableHeight - headerHeight) / rowHeight))
    },
    modelDialogheaders () {
      let headers = [{ text: this.$t('views.App.headerModelName'), value: 'name' }]
      if (this.typeModelDialog === 0) {
        // Ad-hoc Models: Model Name, Client, Brand, Category, Subcategory, Country, Date Modified.
        // The metadata columns (Client, Category, Subcategory) only show when at least one
        // model in the pool has a value for them.
        const pushMeta = key => {
          if (this.availableMetadata(key).length > 0) {
            const cfg = this.metadataFilterConfigs.find(c => c.key === key)
            headers.push({ text: this.$t('views.App.' + cfg.headerKey), value: cfg.valueField })
          }
        }
        pushMeta('client')
        headers.push({ text: this.$t('views.App.headerBrand'), value: 'brandNames' })
        pushMeta('category')
        pushMeta('sub-category')
        headers.push({ text: this.$t('views.App.headerCountry'), value: 'countryName' })
      } else if (this.typeModelDialog === 1) {
        // Custom Models: Model Name, Brand, Category, Reference, Country, Date Modified, Created by
        // Brand only shows when at least one custom model has a brand.
        if (this.availableBrands.length > 0) {
          headers.push({ text: this.$t('views.App.headerBrand'), value: 'brandNames' })
        }
        headers.push({ text: this.$t('views.App.headerCategory'), value: 'reference_model.config.category' })
        headers.push({ text: this.$t('views.App.headerReference'), value: 'custom_model.brand' })
        headers.push({ text: this.$t('views.App.headerCountry'), value: 'custom_model.countryName' })
      }
      headers.push({ text: this.$t('views.App.headerDateModified'), value: 'date_created_formatted' })
      if (this.typeModelDialog === 1 || this.typeModelDialog === 2) {
        // Custom Models and Automatic Models
        headers.push({ text: this.$t('views.App.headerCreatedBy'), value: 'created_by_name' })
      }
      return headers
    },
    availableCountries () {
      // Unique countries scoped to the active tab, so the filter only offers
      // countries that actually appear among the models currently listed. The
      // same country expressed two ways (a registry code from old data vs. a
      // name from client_metadata) is collapsed via resolveCountryKey, and the
      // client_metadata name is preferred for the option label.
      const metadataLabel = new Map() // canonical key -> label sourced from client_metadata (preferred)
      const fallbackLabel = new Map() // canonical key -> label sourced from old data

      const collect = item => {
        const rawValue = this.typeModelDialog === 0
          ? this.getAdHocCountryCode(item)
          : this.getCustomCountryCode(item)
        const key = this.resolveCountryKey(rawValue)
        if (key === null) {
          return
        }
        const label = this.getCountryName(rawValue)
        if (item.client_metadata && item.client_metadata.country) {
          if (!metadataLabel.has(key)) {
            metadataLabel.set(key, label)
          }
        } else if (!fallbackLabel.has(key)) {
          fallbackLabel.set(key, label)
        }
      }

      if (this.typeModelDialog === 0) {
        this.userClients.filter(client => client.type === 'mmm').forEach(collect)
      } else if (this.typeModelDialog === 1) {
        this.userClients.filter(client => client.type !== 'mmm' && client.type !== 'automatic').forEach(collect)
      }

      const keys = new Set([...metadataLabel.keys(), ...fallbackLabel.keys()])
      return Array.from(keys)
        .map(key => ({
          value: key,
          text: metadataLabel.get(key) || fallbackLabel.get(key)
        }))
        .sort((a, b) => a.text.localeCompare(b.text))
    },
    selectAllIcon () {
      const visibleCountries = this.availableCountries.filter(c =>
        !this.countrySearch || c.text.toLowerCase().includes(this.countrySearch.toLowerCase())
      )
      const visibleValues = visibleCountries.map(c => c.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.selectedCountries.includes(v))
      const someVisibleSelected = visibleValues.some(v => this.selectedCountries.includes(v))

      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllText () {
      const visibleCountries = this.availableCountries.filter(c =>
        !this.countrySearch || c.text.toLowerCase().includes(this.countrySearch.toLowerCase())
      )
      if (this.countrySearch && visibleCountries.length > 0) {
        return this.$t('views.App.selectAllVisible', { count: visibleCountries.length })
      }
      return this.$t('views.App.selectAll')
    },
    filteredCountries () {
      if (!this.countrySearch) {
        return this.availableCountries
      }
      return this.availableCountries.filter(c =>
        c.text.toLowerCase().includes(this.countrySearch.toLowerCase())
      )
    },
    hasActiveCountryFilter () {
      // Returns true when some (but not all) countries are selected
      const totalCountries = this.availableCountries.length
      const selectedCount = this.selectedCountries.length
      return selectedCount > 0 && selectedCount < totalCountries
    },
    availableBrands () {
      // Unique brands scoped to the active tab, so the filter only offers
      // brands that actually appear among the models currently listed.
      const brands = new Set()

      if (this.typeModelDialog === 0) {
        // Ad-hoc models - resolved brands (client_metadata.brand or config.brands keys)
        this.userClients.filter(client => client.type === 'mmm').forEach(item => {
          this.getModelBrands(item).forEach(brand => {
            brands.add(brand)
          })
        })
      } else if (this.typeModelDialog === 1) {
        // Custom models - only client_metadata.brand
        this.userClients.filter(client => client.type !== 'mmm' && client.type !== 'automatic').forEach(item => {
          if (item.client_metadata && item.client_metadata.brand) {
            brands.add(item.client_metadata.brand)
          }
        })
      }

      // Convert to array and sort
      return Array.from(brands).sort((a, b) => a.localeCompare(b)).map(brand => ({
        value: brand,
        text: brand
      }))
    },
    filteredBrands () {
      if (!this.brandSearch) {
        return this.availableBrands
      }
      return this.availableBrands.filter(b =>
        b.text.toLowerCase().includes(this.brandSearch.toLowerCase())
      )
    },
    hasActiveBrandFilter () {
      // Returns true when some (but not all) brands are selected
      const totalBrands = this.availableBrands.length
      const selectedCount = this.selectedBrands.length
      return selectedCount > 0 && selectedCount < totalBrands
    },
    selectAllBrandsIcon () {
      const visibleBrands = this.availableBrands.filter(b =>
        !this.brandSearch || b.text.toLowerCase().includes(this.brandSearch.toLowerCase())
      )
      const visibleValues = visibleBrands.map(b => b.value)
      const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(v => this.selectedBrands.includes(v))
      const someVisibleSelected = visibleValues.some(v => this.selectedBrands.includes(v))

      if (allVisibleSelected) {
        return 'mdi-close-box'
      } else if (someVisibleSelected) {
        return 'mdi-minus-box'
      } else {
        return 'mdi-checkbox-blank-outline'
      }
    },
    selectAllBrandsText () {
      const visibleBrands = this.availableBrands.filter(b =>
        !this.brandSearch || b.text.toLowerCase().includes(this.brandSearch.toLowerCase())
      )
      if (this.brandSearch && visibleBrands.length > 0) {
        return this.$t('views.App.selectAllVisible', { count: visibleBrands.length })
      }
      return this.$t('views.App.selectAll')
    },
    modelDialogItems () {
      let items = []
      if (this.typeModelDialog === 0) {
        // Ad-hoc: one row per version family, its latest version; older versions are added below once
        // the filters are applied, as rows of their own when the family is expanded
        items = this.userClients.filter(client => client.type === 'mmm' && this.isLatestVersion(client))

        // Apply country filter if some (but not all) countries are selected
        if (this.selectedCountries.length > 0 && this.selectedCountries.length < this.availableCountries.length) {
          items = items.filter(item => {
            const key = this.resolveCountryKey(this.getAdHocCountryCode(item))
            return key !== null && this.selectedCountries.includes(key)
          })
        }

        // Apply brand filter if some (but not all) brands are selected
        if (this.selectedBrands.length > 0 && this.selectedBrands.length < this.availableBrands.length) {
          items = items.filter(item => {
            // Check if any of the item's resolved brands are in the selected brands
            return this.getModelBrands(item).some(brand => this.selectedBrands.includes(brand))
          })
        }

        // Apply metadata filters (client, category, sub-category) when a strict subset is selected
        this.metadataFilterConfigs.forEach(cfg => {
          const selected = this.metadataSelected[cfg.key]
          const available = this.availableMetadata(cfg.key)
          if (selected.length > 0 && selected.length < available.length) {
            items = items.filter(item => {
              const val = this.getMetadataValue(item, cfg.key)
              return val !== null && selected.includes(val)
            })
          }
        })
      } else if (this.typeModelDialog === 1) {
        // Custom
        items = this.userClients.filter(client => client.type !== 'mmm' && client.type !== 'automatic')

        // Apply country filter if some (but not all) countries are selected
        if (this.selectedCountries.length > 0 && this.selectedCountries.length < this.availableCountries.length) {
          items = items.filter(item => {
            const key = this.resolveCountryKey(this.getCustomCountryCode(item))
            return key !== null && this.selectedCountries.includes(key)
          })
        }

        // Apply brand filter if some (but not all) brands are selected
        if (this.selectedBrands.length > 0 && this.selectedBrands.length < this.availableBrands.length) {
          items = items.filter(item => {
            // Custom models only match on client_metadata.brand
            return item.client_metadata && item.client_metadata.brand && this.selectedBrands.includes(item.client_metadata.brand)
          })
        }
      } else if (this.typeModelDialog === 2) {
        // Automatic
        items = this.userClients.filter(client => client.type === 'automatic')
      }
      if (this.typeModelDialog === 0) {
        const rows = []
        items.forEach(item => {
          rows.push(item)
          if (this.isVersionFamilyExpanded(item)) {
            const versions = this.modelVersionFamilies.get(versionFamilyId(item)) || []
            versions.slice(0, -1).reverse().forEach(version => {
              rows.push({ ...version, isOlderVersion: true })
            })
          }
        })
        items = rows
      }
      // Add formatted fields for proper search functionality
      return items.map(item => {
        const result = {
          ...item,
          date_created_formatted: this.dateModified(item)
        }
        // Add country name for searchable field
        if (this.typeModelDialog === 0) {
          // Ad-hoc models - get country from reference_model.config.brands
          result.countryName = this.getAdHocCountryName(item)
          // Add brand names for searchable field
          result.brandNames = this.getAdHocBrands(item)
          // Add curated metadata fields (flat, for search/sort)
          result.clientName = this.getMetadataValue(item, 'client') || ''
          result.categoryName = this.getMetadataValue(item, 'category') || ''
          result.subcategoryName = this.getMetadataValue(item, 'sub-category') || ''
          if (!item.isOlderVersion) {
            const versions = this.modelVersionFamilies.get(versionFamilyId(item))
            result.versionCount = versions ? versions.length : 1
          }
        } else if (this.typeModelDialog === 1) {
          // Custom models - country prefers client_metadata.country, else custom_model.country
          const countryCode = this.getCustomCountryCode(item)
          result.custom_model = {
            ...item.custom_model,
            countryName: countryCode ? this.getCountryName(countryCode) : ''
          }
          // Brand: only from client_metadata.brand
          result.brandNames = (item.client_metadata && item.client_metadata.brand) ? item.client_metadata.brand : ''
        }
        return result
      })
    },
    // modelDialogItems searched and sorted, older versions right below their current version
    modelDialogTableItems () {
      const search = this.modelDialogSearch ? this.modelDialogSearch.trim() : ''
      const items = search ? filterFamilyRows(this.modelDialogItems, item => this.matchesModelDialogSearch(item, search)) : this.modelDialogItems
      return sortFamilyRows(items, current => this.sortModelDialogRows(current, this.modelDialogSortBy, this.modelDialogSortDesc))
    },
    modelDialogFamilyCount () {
      return countFamilyRows(this.modelDialogTableItems)
    },
    modelDialogPageItems () {
      return familyRowsPage(this.modelDialogTableItems, this.modelDialogPage, this.modelDialogRows)
    },
    modelDialogLastPage () {
      return lastFamilyPage(this.modelDialogFamilyCount, this.modelDialogRows)
    },
    token: function () {
      if (this.$route.query.external_token && this.$route.query.origin && this.authUser.initialized) {
        const externalToken = decodeURIComponent(this.$route.query.external_token)
        const origin = decodeURIComponent(this.$route.query.origin)
        return {
          externalToken: externalToken,
          origin: origin
        }
      } else {
        return null
      }
    },
    oid: function () {
      if (this.$route.query.oid && this.authUser.initialized) {
        const origin = decodeURIComponent(this.$route.query.oid)
        return {
          origin: origin,
          newTab: this.$route.query.popup
        }
      } else {
        return null
      }
    },
    oidAuthorize: function () {
      if (this.$route.path === '/signin-oidc' && this.authUser.initialized) {
        return this.$route
      } else {
        return null
      }
    },
    iframe: function () {
      if ((this.$route.query.iframe || this.$route.query.oid) && this.authUser.initialized) {
        return true
      } else {
        return null
      }
    },
    customView: function () {
      // Custom views
      // 1. growthos
      return this.$route.query.customview
    },
    plain: function () {
      if (this.$route.query.plain && this.authUser.initialized) {
        return true
      } else {
        return null
      }
    },
    isSharedRoute: function () {
      return this.$route.name === 'sharedPlan' || this.$route.name === 'sharedModelReport'
    },
    currentClientLocal: {
      get: function () {
        if (this.userClients.filter(client => client.id === this.currentClient).length > 0) {
          return this.currentClient
        } else {
          return null
        }
      },
      set: function (client) {
        let currentRoute = this.$route.name
        if (currentRoute === 'simulationDetails') {
          currentRoute = 'modelReport'
        }
        this.$router.push({ name: currentRoute }).catch(err => {
          if (err && err.name !== 'NavigationDuplicated') {
            console.log(err)
          } else if (!err) {
            console.log('Warning. Caught exception and no error given (beforeEach related)')
          }
        })
        this.setCurrentClient(client)
      }
    },
    currentClientLogo () {
      const clientArray = this.userClients.filter(client => {
        return client.id === this.currentClientLocal
      })
      if (clientArray.length > 0) {
        return clientArray[0].logo
      } else {
        return null
      }
    }
  },
  watch: {
    // Vuetify only keeps the page in range when it pages the rows itself
    modelDialogLastPage (lastPage) {
      if (this.modelDialogPage > lastPage) {
        this.modelDialogPage = lastPage
      }
    },
    modelDialog (newVal) {
      if (newVal) {
        // When dialog opens, select all countries and brands
        this.selectedCountries = this.availableCountries.map(c => c.value)
        this.selectedBrands = this.availableBrands.map(b => b.value)
      }
      if (!newVal) {
        // Clear country search when dialog closes
        this.countrySearch = ''
        // Reset selected countries when dialog closes
        this.selectedCountries = []
        // Clear brand search when dialog closes
        this.brandSearch = ''
        // Reset selected brands when dialog closes
        this.selectedBrands = []
      }
    },
    typeModelDialog () {
      // availableCountries/availableBrands are scoped to the active tab, so when
      // the tab changes re-select all of the new tab's values (and clear searches)
      // to keep the filter selection consistent with the options now shown.
      if (this.modelDialog) {
        this.countrySearch = ''
        this.brandSearch = ''
        this.selectedCountries = this.availableCountries.map(c => c.value)
        this.selectedBrands = this.availableBrands.map(b => b.value)
      }
    },
    'authUser.isLoggedIn': function (isLoggedIn) {
      // Reset data
      this.logInEmail = ''
      this.logInPassword = ''
      this.tryingLogIn = false
      // Load countries
      this.loadCountries()
      // Check if user needs to accept terms
      if (isLoggedIn && !this.authUser.acceptedTerms) {
        this.showTermsDialog = true
      }
    },
    'authUser.initialized': {
      immediate: true,
      handler: function () {
        // When auth is initialized and user is not logged in, mark clients as initialized
        // since there are no clients to load (prevents loading screen from getting stuck)
        if (this.authUser.initialized && !this.authUser.isLoggedIn) {
          this.setClientsInitialized(true)
          // Clear any stored client ID since the user has no access when not logged in
          if (localStorage.currentClientId) {
            delete localStorage.currentClientId
          }
        }
      }
    },
    'authUser.clients': function () {
      if (!this.authUser.isManager && !this.authUser.isAdmin && !this.authUser.isSupervisor) {
        if (this.authUser.clients && this.authUser.clients.length > 0) {
          this.loadClientsRealtime({
            clients: this.authUser.clients
          })
        } else if (this.authUser.clients && this.authUser.clients.length === 0) {
          // User has no clients, check if they have groups before marking as initialized
          if (!this.authUser.groups || this.authUser.groups.length === 0) {
            this.setClientsInitialized(true)
          }
        }
      }
    },
    'authUser.groups': function () {
      if (!this.authUser.isManager && !this.authUser.isAdmin && !this.authUser.isSupervisor) {
        if (this.authUser.groups && this.authUser.groups.length > 0) {
          this.loadGroupClientsRealtime({
            groups: this.authUser.groups,
            user: this.authUser.uid
          })
        } else if (this.authUser.groups && this.authUser.groups.length === 0) {
          // User has no groups, check if they have clients before marking as initialized
          if (!this.authUser.clients || this.authUser.clients.length === 0) {
            this.setClientsInitialized(true)
          }
        }
      }
    },
    'authUser.isManager': {
      immediate: true,
      handler: function () {
        if (this.authUser.initialized && this.authUser.isManager) {
          this.loadAllClients()
        }
      }
    },
    'authUser.isSupervisor': {
      immediate: true,
      handler: function () {
        if (this.authUser.initialized && this.authUser.isSupervisor) {
          this.loadAllClients()
        }
      }
    },
    'authUser.isAdmin': {
      immediate: true,
      handler: function () {
        if (this.authUser.initialized && this.authUser.isAdmin) {
          this.loadAllClients()
        }
      }
    },
    currentClient: {
      immediate: true,
      handler: function (clientId) {
        // Load this model's simulations app-wide so the menu can tell whether the
        // (hidden) What-If Scenario "Strategy Check" plans exist for it.
        if (clientId) {
          this.loadSimulations({ client: clientId })
        }
      }
    },
    currentClientObject: function (newObj) {
      if (newObj.primary_color) {
        if (!this.basePrimaryColor) {
          this.setBasePrimaryColor(this.$vuetify.theme.themes.light.primary)
        }
        this.$vuetify.theme.themes.light.primary = newObj.primary_color
      } else {
        if (this.basePrimaryColor) {
          this.$vuetify.theme.themes.light.primary = this.basePrimaryColor
        }
      }
    },
    onConfigRouteWithoutConfig: {
      immediate: true,
      handler: function (value) {
        if (value) {
          this.$router.replace({ name: 'home' }).catch(err => {
            if (err && err.name !== 'NavigationDuplicated') {
              console.log(err)
            }
          })
        }
      }
    },
    currentClientLocal: function (newClient, oldClient) {
      if (!newClient) {
        this.$router.push({ name: 'home' }).catch(err => {
          if (err && err.name !== 'NavigationDuplicated') {
            console.log(err)
          } else if (!err) {
            console.log('Warning. Caught exception and no error given (beforeEach related)')
          }
        })
        if (!oldClient) {
          this.noModelSelected()
        } else if (oldClient) {
          this.currentModelDeleted()
        }
      }
    },
    token: {
      handler: function (token) {
        if (token && !this.isLoggedIn) {
          this.signInWithToken({
            externalToken: token.externalToken,
            origin: token.origin
          }).then(result => {
            if (!result) {
              this.tokenError = 'Could not validate user'
            } else {
              console.log('User validated')
            }
          }).catch(error => {
            console.log(error)
          })
        }
      },
      immediate: true
    },
    oid: {
      handler: function (oid) {
        // TODO: Fix properly using Promises
        // Wait for 3 seconds before executing
        setTimeout(() => {
          if (oid && this.authUser.initialized && !this.authUser.isLoggedIn) {
            this.signInWithOid({
              origin: oid.origin,
              newTab: oid.newTab
            }).then(result => {
              if (!result) {
                this.oidError = 'Could not redirect user'
              } else {
                console.log('User redirected')
              }
            }).catch(error => {
              this.oidError = 'Could not redirect user'
              console.log(error)
            })
          }
        }, 3000)
      },
      immediate: true
    },
    oidAuthorize: {
      handler: function (oidAuthorize, oidAuthorizeOld) {
        console.log('oidAuthorize changed')
        console.log('oidAuthorize old: ', oidAuthorizeOld)
        console.log('oidAuthorize new: ', oidAuthorize)
        console.log('User initalized: ', this.authUser.initialized)
        console.log('User logged-in: ', this.authUser.isLoggedIn)
        if (this.oidAuthorize && window.opener && window.opener.location && window.opener.location.origin) {
          const targetIframeOrigin = window.opener.location.origin
          this.sendAuthToOpener({
            targetIframeOrigin: targetIframeOrigin,
            oidAuthorize: oidAuthorize
          })
        } else if (oidAuthorize && oidAuthorize !== oidAuthorizeOld && this.authUser.initialized && !this.authUser.isLoggedIn) {
          this.authorizeWithOid(oidAuthorize).then(result => {
            if (!result) {
              this.oidError = 'Could not authorize user'
              console.log('Could not authorize user')
            } else {
              console.log('User authorized')
              // Closing the window will only work when the window was opened programatically, so this line will not close non-popup enabled logins
              window.close()
              this.$router.push({ name: 'home', query: { iframe: true } })
            }
          }).catch(error => {
            this.oidError = 'Could not authorize user'
            console.log(error)
          })
        }
      },
      immediate: false
    }
  },
  mounted: function () {
    function receiveMessage (event) {
      if (event.data.type === 'auth') {
        this.authorizeWithOid(event.data.oidAuthorize).then(result => {
          if (!result) {
            this.oidError = 'Could not authorize user'
            console.log('Could not authorize user')
          } else {
            console.log('User authorized')
            this.$router.push({ name: 'home', query: { iframe: true } })
          }
        }).catch(error => {
          this.oidError = 'Could not authorize user'
          console.log(error)
        })
      }
    }
    // Bind the function to the current instance
    const boundReceiveMessage = receiveMessage.bind(this)
    window.addEventListener('message', boundReceiveMessage, false)
    // Track viewport height so the Model Selection table can size its rows fluidly
    this.onWindowResize = () => {
      this.windowHeight = window.innerHeight
    }
    window.addEventListener('resize', this.onWindowResize)
    this.initAuth()
    // Load language from localStorage for faster initial render (before auth completes)
    loadLanguageFromStorage(this.$i18n)
    this.$nextTick(() => {
      this.isParamsLoaded = true
    })
  },
  beforeDestroy () {
    if (this.onWindowResize) {
      window.removeEventListener('resize', this.onWindowResize)
    }
  },
  // This is crucial - ensures the flag is reset when route changes
  beforeRouteUpdate (to, from, next) {
    this.isParamsLoaded = false
    next()
    this.$nextTick(() => {
      this.isParamsLoaded = true
    })
  }
}
</script>
<style scoped>
  /* Older versions of an expanded Ad-Hoc family in the Model Selection table */
  .compact-model-table ::v-deep .model-version-row {
    background-color: #f5f5f5;
  }
  .compact-model-table ::v-deep .version-row-spacer {
    visibility: hidden;
  }
  /* Sits right under the Current Model select, whose details area already adds spacing */
  .current-model-version {
    margin-top: -12px;
  }
  .notification-menu {
    max-height: 400px;
    overflow-y: auto;
  }
  .notification-item {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
  .notification-item:last-child {
    border-bottom: none;
  }
  .compact-model-table ::v-deep th {
    height: 30px !important;
    padding: 0 8px !important;
    font-size: 0.7rem !important;
  }
  .compact-model-table ::v-deep td {
    height: 28px !important;
    padding: 0 8px !important;
    font-size: 0.75rem !important;
  }
  .compact-model-table ::v-deep .v-chip.v-size--x-small {
    height: 18px;
  }
  .compact-model-table ::v-deep .na-chip.v-size--x-small {
    height: 14px;
    padding: 0 6px;
    font-size: 0.6rem;
  }
  .compact-model-table ::v-deep tbody tr {
    cursor: pointer;
  }
  .compact-model-table ::v-deep .v-data-footer__select {
    display: none;
  }
  .compact-model-table ::v-deep .v-data-footer {
    justify-content: center;
  }
  .compact-model-table ::v-deep .v-data-footer__icons-before {
    order: 1;
  }
  .compact-model-table ::v-deep .v-data-footer__pagination {
    order: 2;
  }
  .compact-model-table ::v-deep .v-data-footer__icons-after {
    order: 3;
  }
</style>

<style>
  .search-highlight {
    background-color: var(--v-primary-lighten4);
    color: var(--v-primary-darken2);
    font-weight: inherit;
    padding: 0 2px;
    border-radi
